import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

export default function CartDrawer() {
  const { items, updateQty, removeItem, subtotal, open, setOpen, clear } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-background border-l border-border/60 flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-border/60">
          <SheetTitle className="font-display text-2xl tracking-tight flex items-center justify-between">
            Your Order
            <span className="text-sm text-muted-foreground font-normal">
              {items.length} item{items.length !== 1 ? "s" : ""}
            </span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <p className="font-display text-lg">Your cart is empty</p>
              <p className="text-sm mt-2">Add a special or build your own drink</p>
            </div>
          )}

          {items.map((item, idx) => (
            <div
              key={idx}
              data-testid={`cart-item-${idx}`}
              className="bg-white rounded-2xl p-4 border border-border/60"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-display font-medium leading-tight">{item.name}</p>
                  {item.flavors && item.flavors.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Flavors: {item.flavors.join(", ")}
                    </p>
                  )}
                  <p className="text-sm text-primary font-medium mt-2">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
                <button
                  data-testid={`remove-item-${idx}`}
                  onClick={() => removeItem(idx)}
                  className="text-muted-foreground hover:text-destructive transition-colors p-1"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <button
                  data-testid={`decrement-item-${idx}`}
                  onClick={() => updateQty(idx, item.quantity - 1)}
                  className="h-8 w-8 rounded-full border border-border/80 flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <Minus className="h-3 w-3" />
                </button>
                <span data-testid={`item-qty-${idx}`} className="w-6 text-center text-sm font-medium">
                  {item.quantity}
                </span>
                <button
                  data-testid={`increment-item-${idx}`}
                  onClick={() => updateQty(idx, item.quantity + 1)}
                  className="h-8 w-8 rounded-full border border-border/80 flex items-center justify-center hover:bg-muted transition-colors"
                >
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border/60 px-6 py-5 space-y-4 bg-muted/30">
            <div className="flex items-center justify-between">
              <span className="text-sm uppercase tracking-wide text-muted-foreground">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display text-2xl">${subtotal.toFixed(2)}</span>
            </div>
            <Button
              onClick={() => {
                setOpen(false);
                navigate("/checkout");
              }}
              data-testid="checkout-btn"
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 font-display tracking-wide"
            >
              Checkout
            </Button>
            <button
              data-testid="clear-cart-btn"
              onClick={clear}
              className="w-full text-xs text-muted-foreground hover:text-destructive transition-colors"
            >
              Clear cart
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
